import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { MunicipalityData } from "../../../models/MunicipalityData";
import {
  CreatePlacesQueryPayload,
  PlacesQueryResultsSearchParams,
  PlacesQuerySearchParams,
} from "../../../models/PlacesQueryData";
import DataSvc from "../../../services/dataSvc";

const POLL_INTERVAL_MS = 5000;
const DEFAULT_LIMIT = 20;

export type PlacesQueriesFilterFormData = {
  numberPerPage: number;
  pageIndex: number;
};

export type PlacesQueryResultsFilterFormData = {
  numberPerPage: number;
  pageIndex: number;
};

export const useDefaultCategories = () =>
  useQuery(["placesQueriesDefaultCategories"], () =>
    DataSvc.getPlacesQueriesDefaultCategories()
  );

export const usePrefectures = () =>
  useQuery<MunicipalityData[]>(["placesQueriesPrefectures"], () =>
    DataSvc.getMunicipalitiesParent()
  );

export const useCities = (prefectureExternalId?: number) =>
  useQuery<MunicipalityData[]>(
    ["placesQueriesCities", prefectureExternalId],
    () => DataSvc.getMunicipalitiesChild(String(prefectureExternalId)),
    { enabled: !!prefectureExternalId }
  );

export const usePlacesQueriesList = () => {
  const client = useQueryClient();

  const [searchParams, setSearchParams] = useState<PlacesQuerySearchParams>({
    sort: "created_at",
    order: "desc",
    limit: DEFAULT_LIMIT,
    offset: 0,
    seek_total_count: true,
  });

  const filterFormData = useMemo<PlacesQueriesFilterFormData>(() => {
    const numberPerPage = searchParams.limit ?? DEFAULT_LIMIT;
    return {
      numberPerPage,
      pageIndex: Math.floor((searchParams.offset ?? 0) / numberPerPage) + 1,
    };
  }, [searchParams]);

  const handleChangeFilterFormData = (
    filterFormData: PlacesQueriesFilterFormData
  ) => {
    setSearchParams((sp) => ({
      ...sp,
      offset: (filterFormData.pageIndex - 1) * filterFormData.numberPerPage,
    }));
  };

  const query = useQuery({
    queryKey: ["placesQueries", searchParams],
    queryFn: () => DataSvc.getPlacesQueries(searchParams),
  });

  const hasPendingRows = !!query.data?.data.some(
    (item) => item.status === "pending"
  );

  useEffect(() => {
    if (!hasPendingRows) {
      return;
    }
    const interval = setInterval(() => query.refetch(), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPendingRows]);

  const invalidate = () =>
    client.invalidateQueries({ queryKey: ["placesQueries"] });

  return {
    query,
    searchParams,
    setSearchParams,
    filterFormData,
    handleChangeFilterFormData,
    invalidate,
  };
};

export const useCreatePlacesQuery = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePlacesQueryPayload) =>
      DataSvc.createPlacesQuery(payload),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ["placesQueries"] }),
  });
};

export const useDeletePlacesQuery = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => DataSvc.deletePlacesQuery(id),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ["placesQueries"] }),
  });
};

export const usePlacesQuery = (id?: string) => {
  const query = useQuery({
    queryKey: ["placesQuery", id],
    queryFn: () => DataSvc.getPlacesQuery(id as string),
    enabled: !!id,
  });

  const isPending = query.data?.status === "pending";

  useEffect(() => {
    if (!isPending) {
      return;
    }
    const interval = setInterval(() => query.refetch(), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPending]);

  return query;
};

export const usePlacesQueryResults = (id?: string, pollWhilePending = false) => {
  const [searchParams, setSearchParams] =
    useState<PlacesQueryResultsSearchParams>({
      sort: "created_at",
      order: "desc",
      limit: DEFAULT_LIMIT,
      offset: 0,
      seek_total_count: true,
    });

  const filterFormData = useMemo<PlacesQueryResultsFilterFormData>(() => {
    const numberPerPage = searchParams.limit ?? DEFAULT_LIMIT;
    return {
      numberPerPage,
      pageIndex: Math.floor((searchParams.offset ?? 0) / numberPerPage) + 1,
    };
  }, [searchParams]);

  const handleChangeFilterFormData = (
    filterFormData: PlacesQueryResultsFilterFormData
  ) => {
    setSearchParams((sp) => ({
      ...sp,
      offset: (filterFormData.pageIndex - 1) * filterFormData.numberPerPage,
    }));
  };

  const query = useQuery({
    queryKey: ["placesQueryResults", id, searchParams],
    queryFn: () => DataSvc.getPlacesQueryResults(id as string, searchParams),
    enabled: !!id,
  });

  useEffect(() => {
    if (!pollWhilePending) {
      return;
    }
    const interval = setInterval(() => query.refetch(), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollWhilePending]);

  return {
    query,
    searchParams,
    setSearchParams,
    filterFormData,
    handleChangeFilterFormData,
  };
};
